import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { format } from "date-fns";
import { parseScheduleDateLocal } from "../utils/dateUtils";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { get } from "../api/actions";
import MUITable from "../../../components/MUITable";

function DisconnectScheduleTable(props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    scheduledAfter: '',
    scheduledBefore: '',
    page: 1,
    limit: 10,
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const [statusFilter, setStatusFilter] = useState(null);

  const total = props.disconnectSchedules?.data?.disconnectSchedulesList?.total || 0;
  const currentPage = props.disconnectSchedules?.data?.disconnectSchedulesList?.page || 1;
  const currentLimit = props.disconnectSchedules?.data?.disconnectSchedulesList?.limit || 10;

  useEffect(() => {
    props.get(searchParams);
  }, [searchParams]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    const dateObj = moment.isMoment(date) ? date.toDate() : date;
    const formattedDate = dateObj ? format(dateObj, 'yyyy-MM-dd') : '';
    const newParams = {
      ...searchParams,
      scheduledAfter: formattedDate,
      page: 1, // Reset to first page on filter change
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    const dateObj = moment.isMoment(date) ? date.toDate() : date;
    const formattedDate = dateObj ? format(dateObj, 'yyyy-MM-dd') : '';
    const newParams = {
      ...searchParams,
      scheduledBefore: formattedDate,
      page: 1, // Reset to first page on filter change
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    const newParams = {
      ...searchParams,
      scheduledAfter: '',
      scheduledBefore: '',
      page: 1, // Reset to first page on filter change
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleGroupChipClick = (schedule) => {
    const groupId = schedule.groupId || schedule.group?.id || schedule.group?.uuid;
    if (groupId) {
      navigate(`/disconnect-management?groupId=${groupId}`);
    }
  };

  const handleAction = (actionKey, schedule) => {
    switch (actionKey) {
      case 'edit':
        if (props.onEditSchedule) {
          props.onEditSchedule(schedule);
        }
        break;
      case 'delete':
        if (props.onDeleteSchedule) {
          props.onDeleteSchedule(schedule);
        }
        break;
      default:
        break;
    }
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    const newParams = {
      ...searchParams,
      status: status || '',
      page: 1, // Reset to first page on filter change
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleChangePage = (event, newPage) => {
    const newParams = {
      ...searchParams,
      page: newPage + 1, // MUI TablePagination uses 0-based index, backend uses 1-based
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    const newParams = {
      ...searchParams,
      limit: newLimit,
      page: 1, // Reset to first page when changing rows per page
    };
    setSearchParams(newParams);
    props.get(newParams);
  };

  const renderExpandedContent = (schedule) => {
    const group = schedule.group || {};
    const properties = group.properties || group.propertyList || group.property_list || group.items || [];

    return (
      <Table size="small" aria-label="group-properties">
        <TableHead>
          <TableRow>
            <TableCell>Property Name</TableCell>
            <TableCell>Address</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {properties && properties.length > 0 ? (
            properties.map((property) => (
              <TableRow key={property.id || property.uuid}>
                <TableCell component="th" scope="row">
                  {property.name}
                </TableCell>
                <TableCell>
                  {[property.address, property.city, property.state, property.zip]
                    .filter(part => part && part.trim() !== '')
                    .join(', ') || 'Address not available'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={property.active ? "Active" : "Inactive"}
                    color={property.active ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No properties found in this group
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  };

  const columns = [
    {
      field: 'group',
      label: 'Group',
      type: 'chip',
      render: (schedule) => {
        return (
          <Chip
            label={schedule?.groupName || 'Unknown'}
            onClick={(e) => {
              e.stopPropagation();
              handleGroupChipClick(schedule);
            }}
            style={{
              backgroundColor: schedule?.bgColorTag ? `#${schedule.bgColorTag}` : 'transparent',
              color: schedule?.bgColorTag ? '#ffffff' : '#000000',
              border: schedule?.bgColorTag ? 'none' : '1px solid #ccc',
              cursor: 'pointer'
            }}
            variant={schedule?.bgColorTag ? "filled" : "outlined"}
            size="small"
          />
        );
      }
    },
    {
      field: 'scheduledDisconnectDate',
      label: 'Scheduled Date',
      type: 'date',
      render: (schedule) => {
        const date = parseScheduleDateLocal(schedule.scheduledDisconnectDate);
        return date ? format(date, 'MM/dd/yyyy') : 'N/A';
      }
    },
  ];

  const actions = [
    {
      key: 'edit',
      icon: EditIcon,
      tooltip: 'Edit schedule',
      color: 'primary'
    },
    {
      key: 'delete',
      icon: DeleteIcon,
      tooltip: 'Delete schedule',
      color: 'error'
    }
  ];



  const data = props.disconnectSchedules?.data?.disconnectSchedulesList?.items || [];
  const isLoading = props.disconnectSchedules?.isLoading || false;
  const error = props.disconnectSchedules?.error;

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="error">
          Error loading disconnect schedules: {error.message}
        </Typography>
      </Box>
    );
  }

  const getStatusChipStyle = (status) => {
    if (statusFilter === status) {
      switch (status) {
        case "PENDING":
          return {
            backgroundColor: "rgba(255, 193, 7, 0.1)",
            color: "#ff9800 !important",
            border: "1px solid #ff9800",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(255, 193, 7, 0.2)",
              border: "1px solid #ff9800",
            }
          };
        case "COMPLETED":
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        case "CANCELLED":
          return {
            backgroundColor: "rgba(244, 67, 54, 0.1)",
            color: "#f44336 !important",
            border: "1px solid #f44336",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(244, 67, 54, 0.2)",
              border: "1px solid #f44336",
            }
          };
        case null:
          return {
            backgroundColor: "rgba(76, 175, 80, 0.1)",
            color: "#4caf50 !important",
            border: "1px solid #4caf50",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              border: "1px solid #4caf50",
            }
          };
        default:
          return {
            backgroundColor: "rgba(158, 158, 158, 0.1)",
            color: "#757575 !important",
            border: "1px solid #bdbdbd",
            fontWeight: 500,
            borderRadius: "16px",
            fontSize: "0.875rem",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(158, 158, 158, 0.2)",
              border: "1px solid #bdbdbd",
            }
          };
      }
    }
    return {
      backgroundColor: "rgba(158, 158, 158, 0.1)",
      color: "#757575 !important",
      border: "1px solid #bdbdbd",
      fontWeight: 500,
      borderRadius: "16px",
      fontSize: "0.875rem",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "rgba(158, 158, 158, 0.2)",
        border: "1px solid #bdbdbd",
      }
    };
  };

  const StatusFilters = () => {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Button
          onClick={() => handleStatusFilter(null)}
          sx={getStatusChipStyle(null)}
          variant="outlined"
          size="small"
        >
          All Status
        </Button>
        <Button
          onClick={() => handleStatusFilter('PENDING')}
          sx={getStatusChipStyle('PENDING')}
          variant="outlined"
          size="small"
        >
          Pending
        </Button>
        <Button
          onClick={() => handleStatusFilter('COMPLETED')}
          sx={getStatusChipStyle('COMPLETED')}
          variant="outlined"
          size="small"
        >
          Completed
        </Button>
        <Button
          onClick={() => handleStatusFilter('CANCELLED')}
          sx={getStatusChipStyle('CANCELLED')}
          variant="outlined"
          size="small"
        >
          Cancelled
        </Button>
      </Box>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <DatePicker
            label="From Date"
            value={startDate}
            onChange={handleStartDateChange}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 200 }
              }
            }}
          />
          <DatePicker
            label="To Date"
            value={endDate}
            onChange={handleEndDateChange}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 200 }
              }
            }}
          />
          <Button 
            variant="outlined" 
            size="small"
            onClick={handleClearDates}
            disabled={!startDate && !endDate}
          >
            Clear Dates
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              {/*<Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: '#666' }}>*/}
              {/*  Status*/}
              {/*</Typography>*/}
              {/*<StatusFilters />*/}
            </Box>
          </Box>
        </Box>
      </Box>
      
      <MUITable
        data={data}
        columns={columns}
        searchConfig={{
          enabled: false
        }}
        expandableConfig={{
          enabled: true,
          renderExpandedContent,
          expandTriggerField: 'group'
        }}
        actionsConfig={{
          enabled: true,
          actions
        }}
        loadingState={{
          isLoading,
          loadingText: "Loading disconnect schedules..."
        }}
        emptyState={{
          message: "No disconnect schedules found. Try adjusting your date filter.",
          showWhenEmpty: true
        }}
        paginationConfig={{
          enabled: true,
          total: total,
          page: currentPage - 1, // Convert to 0-based index for MUI
          rowsPerPage: currentLimit,
          rowsPerPageOptions: [10, 25, 50, 100],
          onPageChange: handleChangePage,
          onRowsPerPageChange: handleChangeRowsPerPage,
          labelDisplayedRows: ({ from, to, count }) => `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
        }}
        onAction={handleAction}
      />
    </LocalizationProvider>
  );
}

DisconnectScheduleTable.propTypes = {
  disconnectSchedules: PropTypes.object.isRequired,
  get: PropTypes.func.isRequired,
  onEditSchedule: PropTypes.func,
  onDeleteSchedule: PropTypes.func,
};

function State(state) {
  return {
    disconnectSchedules: state.disconnectSchedules,
  };
}

export default connect(State, { get })(DisconnectScheduleTable);
